import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BarChart3, LoaderCircle, Sparkles } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "@/lib/utils";
import { fdeProjectPath, fdeRoutes } from "@/frontend/features/Fde/routes";
import type {
  DataVisualization,
  VisualizationResult,
} from "@/frontend/features/Fde/data/types";
import { useWorkstationPageContext } from "@/frontend/features/AgentChats/workstationPageContext";

const GraphicWalkerCanvas = lazy(
  () => import("@/frontend/features/Fde/data/GraphicWalkerCanvas"),
);

interface Props {
  projectId?: string | null;
  visualizations: DataVisualization[];
  copy: Record<string, string>;
}

export default function DataAnalysisPanel({ projectId, visualizations, copy }: Props) {
  const [selectedPath, setSelectedPath] = useState(visualizations[0]?.path ?? "");
  const [result, setResult] = useState<VisualizationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const visualizationSignature = visualizations.map((item) => `${item.path}:${item.provenance.updated_at ?? ""}`).join("|");
  const previousVisualizationSignature = useRef(visualizationSignature);
  const selected = visualizations.find((item) => item.path === selectedPath) ?? visualizations[0];

  useWorkstationPageContext("forge-saved-analysis", {
    surface: "forge",
    section: "data",
    view: "saved_visualization",
    project: projectId ? { id: projectId } : undefined,
    dataset: selected ? { model: selected.dataset.model, connection_name: selected.dataset.connection_name } : undefined,
    selection: selected ? { path: selected.path, title: selected.title, description: selected.description } : undefined,
    visualization: selected ? { engine: "graphic_walker", spec: selected.spec } : undefined,
    result: result ? { row_count: result.row_count, returned_rows: result.rows.length } : undefined,
  });

  useEffect(() => {
    if (!selected && selectedPath) setSelectedPath("");
    if (selected && selected.path !== selectedPath) setSelectedPath(selected.path);
  }, [selected, selectedPath]);

  useEffect(() => {
    if (previousVisualizationSignature.current !== visualizationSignature && visualizations[0]) {
      setSelectedPath(visualizations[0].path);
    }
    previousVisualizationSignature.current = visualizationSignature;
  }, [visualizationSignature, visualizations]);

  useEffect(() => {
    if (!projectId || !selected) return;
    const controller = new AbortController();
    setResult(null);
    setError(null);
    fetch(fdeProjectPath(`${fdeRoutes.data}/visualizations/query`, projectId), {
      method: "POST",
      credentials: "same-origin",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
      },
      body: JSON.stringify({ path: selected.path }),
    })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || copy.visualization_error);
        return payload as VisualizationResult;
      })
      .then(setResult)
      .catch((requestError: Error) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      });
    return () => controller.abort();
  }, [copy.visualization_error, projectId, selected]);

  if (visualizations.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="mx-auto flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Sparkles className="size-5" />
          </div>
          <h3 className="mt-4 text-base font-semibold">{copy.no_visualizations}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{copy.no_visualizations_description}</p>
          <p className="mt-4 inline-flex rounded-md border border-border bg-muted/20 px-3 py-2 font-mono text-[11px] text-muted-foreground">
            {copy.dashboard_prompt_example}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-cols-[260px_minmax(0,1fr)]">
      <aside className="min-h-0 overflow-y-auto border-r border-border bg-muted/10 p-2">
        <p className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {copy.saved_visualizations}
        </p>
        {visualizations.map((visualization) => (
          <button
            key={visualization.path}
            type="button"
            onClick={() => setSelectedPath(visualization.path)}
            className={cn(
              "mb-1 w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
              visualization.path === selected?.path
                ? "border-primary/30 bg-primary/5"
                : "border-transparent hover:bg-muted/60",
            )}
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <BarChart3 className="size-4 text-primary" />
              <span className="truncate">{visualization.title}</span>
            </span>
            <span className="mt-1 block truncate text-xs text-muted-foreground">
              {visualization.dataset.model}
            </span>
          </button>
        ))}
      </aside>
      <section className="flex min-h-0 min-w-0 flex-col">
        <header className="shrink-0 border-b border-border px-5 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold">{selected?.title}</h3>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {selected?.description || selected?.dataset.connection_name}
              </p>
            </div>
            {result && (
              <span className="shrink-0 rounded-full bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                {result.row_count} {copy.result_rows}
              </span>
            )}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">{copy.versioned_git}</p>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-4">
          {!result && !error && (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
              <LoaderCircle className="size-4 animate-spin" />
              {copy.loading_visualization}
            </div>
          )}
          {error && (
            <div className="flex h-full items-center justify-center text-sm text-destructive">{error}</div>
          )}
          {result && result.rows.length === 0 && (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {copy.empty_visualization}
            </div>
          )}
          {result && result.rows.length > 0 && selected && (
            <ErrorBoundary
              resetKeys={[selected.path, result.model]}
              fallbackRender={() => (
                <div className="flex h-full items-center justify-center text-sm text-destructive">
                  {copy.visualization_error}
                </div>
              )}
            >
              <Suspense fallback={<div className="h-full animate-pulse rounded-xl bg-muted/40" />}>
                <GraphicWalkerCanvas
                  rows={result.rows}
                  spec={selected.spec}
                  errorLabel={copy.visualization_error}
                />
              </Suspense>
            </ErrorBoundary>
          )}
        </div>
      </section>
    </div>
  );
}
